// Licenses Module
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';

function openModal(id) {
    const m = document.getElementById(id);
    if (m) m.classList.remove('hidden');
}

function closeModal(id) {
    const m = document.getElementById(id);
    if (m) m.classList.add('hidden');
}

let licensesUiBound = false;
function ensureLicensesUi() {
    if (licensesUiBound) {
        return;
    }
    licensesUiBound = true;
    const btn = document.getElementById('btn-generate-license');
    if (btn) {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            openAddLicenseModalFromLicenses();
        });
    }
}

export async function loadLicenses() {
    ensureLicensesUi();
    try {
        const licenses = await api.getLicenses();
        console.log('[Licenses] Loaded:', licenses);
        updateLicensesUI(licenses);
        updateLicenseStats(licenses);
    } catch (error) {
        console.error('[Licenses] Failed to load:', error);
        showNotification('Failed to load licenses', 'error');
    }
}

function updateLicenseStats(licenses) {
    if (!licenses || !Array.isArray(licenses)) {
        console.warn('[Licenses] No licenses data for stats');
        return;
    }

    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    const total = licenses.length;
    const active = licenses.filter(l => l.status === 'active').length;

    const expired = licenses.filter(l => {
        if (l.status === 'expired' || l.status === 'revoked' || l.status === 'inactive') return true;
        const expDate = l.expires_at || l.end_date;
        if (!expDate || expDate === '0001-01-01T00:00:00Z') return false;
        return new Date(expDate) < now;
    }).length;

    const expiring = licenses.filter(l => {
        if (l.status !== 'active') return false;
        const expDate = l.expires_at || l.end_date;
        if (!expDate || expDate === '0001-01-01T00:00:00Z') return false;
        const exp = new Date(expDate);
        return exp > now && exp <= thirtyDaysFromNow;
    }).length;

    const updateStat = (id, value) => {
        const el = document.getElementById(id);
        if (el) el.textContent = value.toLocaleString();
    };

    updateStat('stat-total-licenses', total);
    updateStat('stat-active-licenses', active);
    updateStat('stat-expired-licenses', expired);
    updateStat('stat-expiring-licenses', expiring);

    console.log('[Licenses] Stats updated:', { total, active, expired, expiring });
}

function updateLicensesUI(licenses) {
    try {
        const tbody = document.getElementById('licenses-table-body');
        if (!tbody) {
            console.warn('[Licenses] Table body not found');
            return;
        }

        if (!licenses || licenses.length === 0) {
            tbody.innerHTML = '<tr><td colspan="8" class="py-8 text-center text-slate-500">No licenses found</td></tr>';
            return;
        }

        tbody.innerHTML = licenses.map(license => {
            const key = license.key || license.license_key || '-';
            const pkg = license.package_name || license.package || '-';
            const start = formatDate(license.start_date);
            const end = formatDate(license.expires_at || license.end_date);
            const status = license.status || 'unknown';
            const statusClass = status === 'active' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300';
            const src = license.source || 'license';
            const canRevoke = src === 'license' && license.id > 0 && status === 'active';

            const actions = canRevoke
                ? `<button type="button" onclick="window.revokeLicense(${license.id})" class="text-red-500 hover:text-red-400 text-xs">
                        <i class="fas fa-ban mr-1"></i> Revoke
                   </button>`
                : (src === 'agent'
                    ? '<span class="text-slate-600 text-xs">Managed Systems</span>'
                    : '<span class="text-slate-600">—</span>');

            return `
            <tr class="border-b border-slate-800 hover:bg-slate-900/50">
                <td class="py-4 px-4 font-mono text-xs text-neonCyan">${escapeHtml(key)}</td>
                <td class="py-4 px-4">${escapeHtml(license.client_name || '-')}</td>
                <td class="py-4 px-4 text-slate-400">${escapeHtml(license.server_ip || '-')}</td>
                <td class="py-4 px-4">${escapeHtml(pkg)}</td>
                <td class="py-4 px-4 text-slate-500 text-xs">${start}</td>
                <td class="py-4 px-4 text-slate-500 text-xs">${end}</td>
                <td class="py-4 px-4">
                    <span class="px-2 py-1 ${statusClass} rounded text-xs">${escapeHtml(status)}</span>
                </td>
                <td class="py-4 px-4 text-right">${actions}</td>
            </tr>`;
        }).join('');

        console.log('[Licenses] Rendered', licenses.length, 'licenses');
    } catch (error) {
        console.error('[Licenses] Error updating UI:', error);
    }
}

function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function formatDate(dateStr) {
    if (!dateStr || dateStr === '0001-01-01T00:00:00Z') return '-';
    try {
        return new Date(dateStr).toLocaleDateString();
    } catch (e) {
        return '-';
    }
}

export async function openAddLicenseModal(customerId) {
    document.querySelectorAll('[id^="customer-actions-"]').forEach(el => el.classList.add('hidden'));

    const packages = await api.getPackages();
    const packageSelect = document.getElementById('license-package-select');
    if (packageSelect) {
        packageSelect.innerHTML = '<option value="">Select Package</option>' +
            packages.filter(pkg => pkg.is_active).map(pkg =>
                `<option value="${pkg.id}">${pkg.name} (${pkg.type || 'Paid'}) - $${pkg.price}</option>`
            ).join('');
    }

    const customerIdField = document.getElementById('license-customer-id');
    if (customerIdField) customerIdField.value = customerId;

    const serverIPField = document.getElementById('license-server-ip');
    if (serverIPField) serverIPField.value = '';

    closeModal('addLicenseModalFromLicenses');
    openModal('addLicenseModal');
}

export async function openAddLicenseModalFromLicenses() {
    try {
        const customers = await api.getClients();
        const customerSelect = document.getElementById('license-customer-select');
        if (customerSelect) {
            customerSelect.innerHTML = '<option value="">Select Customer</option>' +
                customers.map(cust =>
                    `<option value="${cust.id}">${cust.name || cust.company || 'N/A'} - ${cust.email || ''}</option>`
                ).join('');

            customerSelect.onchange = async function () {
                const customerId = parseInt(this.value, 10);
                if (customerId) {
                    await openAddLicenseModal(customerId);
                }
            };
        }
        openModal('addLicenseModalFromLicenses');
    } catch (error) {
        console.error(error);
        showNotification('Failed to load customers: ' + error.message, 'error');
    }
}

export async function handleCreateLicense(e) {
    e.preventDefault();
    const form = e.target;
    const customerId = parseInt(form.customer_id.value, 10);
    const serverIP = form.server_ip.value;
    const packageId = parseInt(form.package_id.value, 10);

    if (!customerId || !serverIP || !packageId) {
        showNotification('Please fill in all required fields', 'error');
        return;
    }

    try {
        await api.createLicense({
            customer_id: customerId,
            server_ip: serverIP,
            package_id: packageId
        });
        closeModal('addLicenseModal');
        form.reset();
        await loadLicenses();
        showNotification('License created successfully', 'success');
    } catch (error) {
        showNotification('Failed to create license: ' + error.message, 'error');
    }
}

export async function revokeLicense(id) {
    if (!id) return;
    if (!confirm('Are you sure you want to revoke this license?')) return;

    try {
        await api.revokeLicense(id);
        showNotification('License revoked successfully', 'success');
        loadLicenses();
    } catch (error) {
        console.error('[Licenses] Failed to revoke:', error);
        showNotification('Failed to revoke license: ' + error.message, 'error');
    }
}

window.revokeLicense = revokeLicense;
window.loadLicenses = loadLicenses;
window.openAddLicenseModalFromLicenses = openAddLicenseModalFromLicenses;
window.openAddLicenseModal = openAddLicenseModal;
window.handleCreateLicense = handleCreateLicense;
window.closeModal = closeModal;
window.openModal = openModal;
